import { Grid2 as Grid } from '@mui/material';
import { HEALTHCHECK_NAMES } from 'domain/shared/newProject';
import HealthcheckRow from '../HealthcheckRow';
import { getHealthcheckByName, HealthcheckMessage } from '../../shared/healthcheck';
import { HEALTHCHECK_LABELS } from '../commercial/constants';
import { ControlData } from '../types';
import { useState } from 'react';
import ProgressUpdateModal from './modals/ProgressUpdateModal';
import ForecastBudgetCompletionUpdateModal from './modals/ForecastBudgetCompletionUpdateModal';
import ForecastCompletionDateUpdateModal from './modals/ForecastCompletionDateUpdateModal';
import { getBudgetMessageOverride, getProgressMessageOverride, getTimelineMessageOverride } from './utils';
import { useDateService } from 'framework/utils';

type ProjectManagementHealthchecksProps = {
  data: ControlData;
  refetchControlsData: () => Promise<unknown>;
};

const ProjectManagementHealthchecks = ({ data, refetchControlsData }: ProjectManagementHealthchecksProps) => {
  const healthChecks = data?.health?.healthChecks || [];
  const [openModal, setIsModalOpen] = useState(false);
  const [openBudgetModal, setIsBudgetModalOpen] = useState(false);
  const [openTimelineModal, setIsTimelineModalOpen] = useState(false);
  const { formatDate } = useDateService();

  const businessOutcomesHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.BUSINESS_OUTCOMES);
  const milestonesHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.MILESTONES);

  const progressValueHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.PROGRESS_VALUE);
  const progressDateHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.PROGRESS_DATE);

  const forecastBudgetValueHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.BUDGET_FORECAST_VALUE);
  const forecastBudgetDateHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.BUDGET_FORECAST_DATE);

  const forecastingTimelineValueHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.TIMELINE_VALUE);
  const forecastingTimelineDateHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.TIMELINE_DATE);

  const customerReportHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.CUSTOMER_REPORT);
  const stakeholdersHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.STAKEHOLDERS);
  const risksHealthcheck = getHealthcheckByName(healthChecks, HEALTHCHECK_NAMES.PROJECT_MANAGEMENT_RISKS);
  const progressMessageOverride = getProgressMessageOverride(data);
  const budgetMessageOverride = getBudgetMessageOverride(data);
  const timelineMessageOverride = getTimelineMessageOverride(data, formatDate);

  return (
    <>
      <Grid container direction={'column'} size={12} pl={2}>
        <HealthcheckRow
          label={HEALTHCHECK_LABELS.BUSINESS_OUTCOMES}
          lastUpdateValue={
            businessOutcomesHealthcheck?.latestEntityModificationDate ?? businessOutcomesHealthcheck?.zoneSince
          }
        >
          <HealthcheckMessage healthcheck={businessOutcomesHealthcheck} />
        </HealthcheckRow>
        <HealthcheckRow
          label={HEALTHCHECK_LABELS.MILESTONES}
          lastUpdateValue={milestonesHealthcheck?.latestEntityModificationDate ?? milestonesHealthcheck?.zoneSince}
        >
          <HealthcheckMessage healthcheck={milestonesHealthcheck} />
        </HealthcheckRow>
        {data.settings?.forecastSettings?.isProjectForecastTrackingEnabled && (
          <>
            <HealthcheckRow
              label={HEALTHCHECK_LABELS.PROGRESS}
              lastUpdateValue={
                progressValueHealthcheck
                  ? (progressValueHealthcheck?.latestEntityModificationDate ?? progressValueHealthcheck?.zoneSince)
                  : (progressDateHealthcheck?.latestEntityModificationDate ?? progressDateHealthcheck?.zoneSince)
              }
              isUpdateVisible
              onEdit={() => setIsModalOpen(true)}
            >
              <Grid direction="column" sx={{ justifyContent: 'flex-end' }}>
                {progressValueHealthcheck && (
                  <HealthcheckMessage
                    healthcheck={progressValueHealthcheck}
                    messageOverride={progressMessageOverride}
                    tooltipTitle={`Planned  / Actual `}
                  />
                )}
                <HealthcheckMessage healthcheck={progressDateHealthcheck} />
              </Grid>
            </HealthcheckRow>
            <HealthcheckRow
              label={HEALTHCHECK_LABELS.BUDGET}
              lastUpdateValue={
                forecastBudgetValueHealthcheck
                  ? (forecastBudgetValueHealthcheck?.latestEntityModificationDate ??
                    forecastBudgetValueHealthcheck?.zoneSince)
                  : (forecastBudgetDateHealthcheck?.latestEntityModificationDate ??
                    forecastBudgetDateHealthcheck?.zoneSince)
              }
              isUpdateVisible
              onEdit={() => setIsBudgetModalOpen(true)}
            >
              <Grid direction="column" sx={{ justifyContent: 'flex-end' }}>
                {forecastBudgetValueHealthcheck && (
                  <HealthcheckMessage
                    healthcheck={forecastBudgetValueHealthcheck}
                    messageOverride={budgetMessageOverride}
                    tooltipTitle={`Planned / Forecast `}
                  />
                )}
                <HealthcheckMessage healthcheck={forecastBudgetDateHealthcheck} />
              </Grid>
            </HealthcheckRow>
            <HealthcheckRow
              label={HEALTHCHECK_LABELS.TIMELINE}
              lastUpdateValue={
                forecastingTimelineValueHealthcheck
                  ? (forecastingTimelineValueHealthcheck?.latestEntityModificationDate ??
                    forecastingTimelineValueHealthcheck?.zoneSince)
                  : (forecastingTimelineDateHealthcheck?.latestEntityModificationDate ??
                    forecastingTimelineDateHealthcheck?.zoneSince)
              }
              isUpdateVisible
              onEdit={() => setIsTimelineModalOpen(true)}
            >
              <Grid direction="column">
                {forecastingTimelineValueHealthcheck && (
                  <HealthcheckMessage
                    healthcheck={forecastingTimelineValueHealthcheck}
                    messageOverride={timelineMessageOverride}
                    tooltipTitle={`Planned / Forecast`}
                  />
                )}
                <HealthcheckMessage healthcheck={forecastingTimelineDateHealthcheck} />
              </Grid>
            </HealthcheckRow>
          </>
        )}
        {customerReportHealthcheck && (
          <HealthcheckRow
            label={HEALTHCHECK_LABELS.CUSTOMER_REPORT}
            lastUpdateValue={
              customerReportHealthcheck?.latestEntityModificationDate ?? customerReportHealthcheck?.zoneSince
            }
          >
            <HealthcheckMessage healthcheck={customerReportHealthcheck} />
          </HealthcheckRow>
        )}
        <HealthcheckRow
          label={HEALTHCHECK_LABELS.STAKEHOLDERS}
          lastUpdateValue={stakeholdersHealthcheck?.latestEntityModificationDate ?? stakeholdersHealthcheck?.zoneSince}
        >
          <HealthcheckMessage healthcheck={stakeholdersHealthcheck} />
        </HealthcheckRow>
        <HealthcheckRow
          label={HEALTHCHECK_LABELS.RISKS}
          lastUpdateValue={risksHealthcheck?.latestEntityModificationDate ?? risksHealthcheck?.zoneSince}
        >
          <HealthcheckMessage healthcheck={risksHealthcheck} />
        </HealthcheckRow>
      </Grid>
      <ProgressUpdateModal
        data={data}
        isOpen={openModal}
        onClose={() => setIsModalOpen(false)}
        refetchControlsData={refetchControlsData}
      />
      <ForecastBudgetCompletionUpdateModal
        data={data}
        isOpen={openBudgetModal}
        onClose={() => setIsBudgetModalOpen(false)}
        refetchControlsData={refetchControlsData}
      />
      <ForecastCompletionDateUpdateModal
        data={data}
        isOpen={openTimelineModal}
        onClose={() => setIsTimelineModalOpen(false)}
        refetchControlsData={refetchControlsData}
      />
    </>
  );
};

export default ProjectManagementHealthchecks;
